import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useInlineHighlightsQuery$data,
	useInlineHighlightsQuery$variables,
} from './__generated__/useInlineHighlightsQuery.graphql.ts';

type CommentsNodes = NonNullable<NonNullable<useInlineHighlightsQuery$data['comments']>['nodes']>;

const UseInlineHighlightsQuery = graphql`
	query useInlineHighlightsQuery($pageId: ID!) {
		comments(pageId: $pageId, type: UNRESOLVED, depth: ROOT, first: 1000) {
			nodes {
				id
				location {
					type
					... on InlineComment {
						inlineMarkerRef
					}
				}
				replies {
					id
				}
			}
		}
	}
`;

type useInlineHighlightsProps = {
	pageId: string;
	shouldFireQuery?: boolean;
};

type markerRefMapType = { [key: string]: string };

/* Not Importing these FF's since it causes circular dependency */

export const useInlineHighlights = ({
	pageId,
	shouldFireQuery = true,
}: useInlineHighlightsProps) => {
	const { data, loading } = useQueryInterop<
		useInlineHighlightsQuery$data,
		useInlineHighlightsQuery$variables
	>(UseInlineHighlightsQuery, {
		variables: { pageId },
		skip: !Boolean(shouldFireQuery),
	});

	let markerRefs: string[] = [];
	let markerRefMap: markerRefMapType = {};

	const comments = data?.comments?.nodes;

	if (comments && Array.isArray(comments)) {
		// We need to create a map that connects markerRefs to commentId, we need commentId to fire analytic event
		// https://github.com/microsoft/TypeScript/issues/17002 - for type casting
		markerRefMap = (comments as CommentsNodes).reduce<markerRefMapType>(
			(inlineRefAccumulator, comment) => {
				if (!comment) {
					return inlineRefAccumulator;
				}

				const { inlineMarkerRef } = comment.location;

				// We should always get top-level inline comments, but if this doesn't exist, just return
				if (!inlineMarkerRef) {
					return inlineRefAccumulator;
				}

				inlineRefAccumulator[comment?.id] = inlineMarkerRef;

				comment?.replies?.forEach((reply) => {
					if (reply) {
						const { id: replyId } = reply;
						inlineRefAccumulator[replyId] = inlineMarkerRef;
					}
				});

				return inlineRefAccumulator;
			},
			{},
		);

		markerRefs = [...new Set(Object.values(markerRefMap))];
	}

	return {
		markerRefs,
		markerRefMap,
		loading,
	};
};
